import React, { useState, useRef } from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Target, Clock, CheckCircle, Award, Shield } from 'lucide-react';

const QRMFinancialLab = () => {
  const [clientInfo, setClientInfo] = useState({
    name: '',
    function: '', 
    company: '',
    website: '',
    email: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const fileInputRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    setUploadError('');
    
    if (files.length === 0) return;
    
    if (files.length + uploadedFiles.length > 5) {
      setUploadError('Maximum 5 files allowed total');
      return;
    }
    
    // Check file types
    const allowedTypes = ['.pdf', '.xlsx', '.xls'];
    const invalidFiles = files.filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return !allowedTypes.includes(extension);
    });
    
    if (invalidFiles.length > 0) {
      setUploadError('Only PDF and Excel files are allowed');
      return;
    }
    
    const newFiles = files.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      type: file.name.toLowerCase().includes('.pdf') ? 'PDF' : 'Excel'
    }));
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
    console.log('Files successfully added:', newFiles);
  };

  const handleFileInputChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.target.files);
    processFiles(files);
    
    // Immediately clear the input to prevent browser navigation
    e.target.value = '';
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const startAnalysis = async () => {
    if (!clientInfo.name || !clientInfo.email || !clientInfo.company) {
      setUploadError('Please fill in required fields: Name, Email, Company');
      return;
    }
    
    // Start analysis with progress
    setAnalysisProgress(10);
    
    // Simulate analysis stages
    setTimeout(() => setAnalysisProgress(30), 800);
    setTimeout(() => setAnalysisProgress(60), 1600);
    setTimeout(() => setAnalysisProgress(90), 2400);
    setTimeout(() => {
      setAnalysisProgress(100);
      setShowResults(true);
    }, 3200);
  };

  const sendEmailWithResults = () => {
    const filesList = uploadedFiles.map(f => f.name).join(', ') || 'No files uploaded';
    const subject = 'URGENT: Financial Analysis Results - €1.2M+ Improvement Opportunity - ' + clientInfo.company;
    const body = 'Hello Jan,\n\nCLIENT INFORMATION:\nName: ' + clientInfo.name + '\nFunction: ' + clientInfo.function + '\nCompany: ' + clientInfo.company + '\nEmail: ' + clientInfo.email + '\nWebsite: ' + clientInfo.website + '\n\nFILES ANALYZED:\n' + filesList + '\n\nCRITICAL FINDINGS:\n\nISSUE #1: Working Capital Crisis\n- Cash trapped in inventory/WIP\n- Below optimal efficiency\n- Monthly opportunity cost identified\n\nISSUE #2: EBITDA Underperformance  \n- Current margin below industry benchmark\n- Annual profit gap identified\n- Process efficiency improvements needed\n\nISSUE #3: Cash Conversion Inefficiency\n- Cycle time above best practice\n- Operational bottlenecks identified\n- Competitive risk from faster companies\n\nTOTAL IMPROVEMENT POTENTIAL: Significant annual gains\n\nREQUEST FOR STRATEGIC DISCUSSION:\n1. Immediate working capital optimization\n2. QRM implementation roadmap\n3. Investment timeline and ROI projections\n\nPlease advise on availability for strategic consultation.\n\nBest regards,\n' + clientInfo.name + '\n' + clientInfo.company;
    
    window.open('mailto:info@smartqrmnavigator.com?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <PageSEO path="/financial-lab" />
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* CONVERSION-OPTIMIZED OPENER - Pain Point & Result */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              QRM Financial Intelligence Lab:
              <span className="text-red-600"> Are Your Competitors Outperforming You?</span>
            </h1>
            <p className="text-2xl md:text-3xl text-gray-800 mb-4 font-medium">
              Unlock hidden profit drivers — with total data privacy.
            </p>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Get higher margins, better cash flow, and more profit through competitive financial intelligence
            </p>
          </div>

          {/* URGENCY & RISK - Concrete Examples */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-yellow-50 to-red-50 border-red-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-bold text-red-800 mb-4">💀 The Gap Widens Every Month</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1 text-lg">⚠️</div>
                    <span><strong>Competitor B could be operating at 22% EBITDA while you struggle at 12%</strong></span>
                  </li>
                  <li className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1 text-lg">⚠️</div>
                    <span><strong>Competitor A might have 40% better cash flow</strong> (and you'd never know)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1 text-lg">⚠️</div>
                    <span><strong>You're operating blind</strong> while they steal market share</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-green-200">
                <h3 className="text-xl font-bold text-green-800 mb-4">🎯 What Our Analysis Delivers</h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Get a confidential report showing exactly where you stand, what your competitors do better, and your precise action plan to overtake them.</strong></p>
                  <ul className="space-y-2 mt-4">
                    <li>✨ <strong>Performance ranking:</strong> Where you rank 1-4 vs competitors</li>
                    <li>✨ <strong>Hidden opportunities:</strong> €500K+ gaps you can close</li>
                    <li>✨ <strong>Strategic advantages:</strong> Where YOU win (leverage this!)</li>
                    <li>✨ <strong>Action plan:</strong> Specific moves to dominate your market</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* SECURITY/PRIVACY - Short and Clear */}
          <div className="text-center mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-bold text-blue-900 mb-2">🔒 Total Data Privacy Guarantee</h3>
            <p className="text-blue-800">
              <strong>Your data is encrypted, processed in-memory, and deleted within 48h. 100% GDPR-compliant.</strong>
            </p>
          </div>

          {/* Privacy & Security Notice */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Shield className="mr-3 h-6 w-6 text-green-600" />
              Your Data Privacy & Security Guarantee
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🔒 AES-256 Encryption</h3>
                <p className="text-gray-600 text-sm">Your financial data is encrypted with military-grade security from upload to deletion</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">⏰ Auto-Delete Within 48h</h3>
                <p className="text-gray-600 text-sm">Files automatically deleted after analysis - maximum 48 hours, usually within minutes</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🛡️ GDPR Compliant</h3>
                <p className="text-gray-600 text-sm">Full privacy protection with explicit consent and zero permanent storage</p>
              </div>
            </div>
            
            {/* Privacy Details */}
            <div className="mt-8 p-6 bg-white border border-green-200 rounded-lg">
              <h4 className="text-lg font-bold text-green-900 mb-4">🔒 Privacy Protection Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h5 className="font-semibold text-green-800 mb-2">✅ What We DO:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Encrypt files with AES-256 immediately upon upload</li>
                    <li>• Process in secure memory only (never touch disk)</li>
                    <li>• Delete files immediately after analysis</li>
                    <li>• Require explicit consent for all processing</li>
                    <li>• Provide detailed financial analysis & recommendations</li>
                    <li>• Log metadata only for audit compliance</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-green-800 mb-2">❌ What We DON'T Do:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Store files permanently anywhere</li>
                    <li>• Save financial data in databases</li>
                    <li>• Share data with any third parties</li>
                    <li>• Keep copies after analysis completion</li>
                    <li>• Log financial content (metadata only)</li>
                    <li>• Retain data beyond 48 hours maximum</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-green-100 rounded-lg border border-green-300">
                <p className="text-green-800 text-sm text-center">
                  <strong>🛡️ GUARANTEE:</strong> Your financial data is encrypted, processed securely, and automatically deleted. 
                  We comply with GDPR and maintain no permanent storage of your sensitive information.
                </p>
              </div>
            </div>
          </Card>

          {/* STRONG CLEAR CTA WITH TRUST ELEMENTS */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              📞 Book Your Confidential Intake with Jan Pauwels
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              LEAN LEAD Winner 2026 • Managing Director • 10 Years QRM Expertise
            </p>
            
            {/* Trust Badges */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 bg-white rounded-lg shadow-sm border border-yellow-200">
                <div className="text-3xl mb-2">🏆</div>
                <div className="text-lg font-bold text-yellow-600">LEAN LEAD Winner</div>
                <div className="text-sm text-gray-600">Best QRM Practice 2026</div>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm border border-green-200">
                <div className="text-3xl mb-2">🔒</div>
                <div className="text-lg font-bold text-green-600">GDPR Certified</div>
                <div className="text-sm text-gray-600">AES-256 Encryption</div>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm border border-blue-200">
                <div className="text-3xl mb-2">📈</div>
                <div className="text-lg font-bold text-blue-600">95% Revenue Growth</div>
                <div className="text-sm text-gray-600">Proven Results</div>
              </div>
            </div>
            
            <div className="space-y-4">
              <Button 
                size="lg"
                className="px-12 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-xl shadow-xl"
                onClick={() => window.open('mailto:info@smartqrmnavigator.com?subject=CONFIDENTIAL INTAKE REQUEST - Competitive Intelligence Analysis&body=Hello Jan,\n\nI want to book a confidential intake for competitive financial analysis.\n\nMy company details:\n• Company: [Your Company Name]\n• Name: [Your Name]\n• Email: [Your Email]\n• Industry: [Your Industry]\n\nI need to understand:\n• How my company compares to competitors financially\n• Hidden profit opportunities I might be missing\n• Specific action plan to outperform competition\n\nPlease advise on:\n• Confidential intake process\n• Secure data upload procedures\n• Expected timeline and deliverables\n• Investment required\n\nConfidential discussion needed urgently.\n\nBest regards', '_blank')}
              >
                <Award className="mr-3 h-6 w-6" />
                📞 BOOK CONFIDENTIAL INTAKE NOW
              </Button>
              
              <div className="text-center">
                <p className="text-purple-700 text-sm font-medium">
                  Limited monthly capacity • Confidential analysis • Strategic advantage
                </p>
                <p className="text-gray-600 text-xs mt-2">
                  Response within 24 hours • Jan Pauwels personal attention
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Request Financial Analysis</h2>
            
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Full Name *</Label>
                  <Input
                    value={clientInfo.name}
                    onChange={(e) => setClientInfo(prev => ({...prev, name: e.target.value}))}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Function/Title *</Label>
                  <Input
                    value={clientInfo.function}
                    onChange={(e) => setClientInfo(prev => ({...prev, function: e.target.value}))}
                    placeholder="CEO, Plant Manager, CFO..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Company Name *</Label>
                  <Input
                    value={clientInfo.company}
                    onChange={(e) => setClientInfo(prev => ({...prev, company: e.target.value}))}
                    placeholder="Company Ltd."
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Email Address *</Label>
                  <Input
                    type="email"
                    value={clientInfo.email}
                    onChange={(e) => setClientInfo(prev => ({...prev, email: e.target.value}))}
                    placeholder="john.smith@company.com"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Company Website</Label>
                <Input
                  value={clientInfo.website}
                  onChange={(e) => setClientInfo(prev => ({...prev, website: e.target.value}))}
                  placeholder="https://www.company.com"
                />
              </div>
              
              {/* File Upload Section */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Upload Balance Sheets & P&L Statements</Label>
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.xlsx,.xls"
                    onChange={handleFileInputChange}
                    className="hidden"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="text-3xl mb-3">📊</div>
                  <p className="text-gray-600 mb-3">
                    {isDragging 
                      ? 'Drop your files here...' 
                      : 'Drag & drop your financial statements here, or click to browse'
                    }
                  </p>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Use setTimeout to avoid event conflicts
                      setTimeout(() => {
                        if (fileInputRef.current) {
                          fileInputRef.current.click();
                        }
                      }, 50);
                    }}
                  >
                    Choose Files
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">Max 5 files • PDF/Excel only</p>
                </div>
                
                {/* Error Display */}
                {uploadError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm">{uploadError}</p>
                  </div>
                )}
                
                {/* Uploaded Files Display */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className="font-medium text-gray-900 mb-3">Uploaded Files ({uploadedFiles.length}/5):</p>
                    <div className="space-y-2">
                      {uploadedFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">📄</span>
                            <div>
                              <div className="font-medium text-gray-900">{file.name}</div>
                              <div className="text-sm text-gray-500">{file.size}</div>
                            </div>
                          </div>
                          <Button 
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
                            onClick={() => removeFile(file.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="text-center">
                <Button 
                  size="lg"
                  className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                  onClick={startAnalysis}
                >
                  <Award className="mr-2 h-5 w-5" />
                  Start Financial Analysis
                </Button>
              </div>
            </div>
          </Card>
          
          {/* Analysis Progress */}
          {analysisProgress > 0 && analysisProgress < 100 && (
            <Card className="p-8 mb-8 bg-blue-50 border border-blue-200">
              <h3 className="text-xl font-bold text-blue-900 mb-4">🔄 Analyzing Your Financial Data...</h3>
              <div className="w-full bg-blue-200 rounded-full h-4 mb-3">
                <div 
                  className="bg-blue-600 h-4 rounded-full transition-all duration-500" 
                  style={{width: analysisProgress + '%'}}
                ></div>
              </div>
              <div className="flex justify-between text-sm text-blue-700">
                <span>Processing financial statements and calculating key ratios...</span>
                <span>{analysisProgress}%</span>
              </div>
            </Card>
          )}

          {/* Analysis Results - THE DETAILED RESULTS YOU WANT */}
          {showResults && (
            <Card className="p-8 mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <CheckCircle className="mr-3 h-6 w-6 text-green-600" />
                Financial Analysis Results - Critical Insights Identified
              </h3>
              
              {/* Executive Summary Alert */}
              <div className="bg-red-50 border border-red-300 rounded-lg p-6 mb-8">
                <h4 className="text-xl font-bold text-red-800 mb-3">🚨 URGENT: 3 Critical Issues Requiring Immediate Action</h4>
                <p className="text-red-700 leading-relaxed">
                  Our analysis reveals significant financial inefficiencies that are costing your company 
                  <strong> €500K+ annually</strong> in missed opportunities. These issues require expert QRM intervention 
                  to prevent further deterioration and unlock hidden value in your operations.
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Critical Issues */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">⚡ CRITICAL: 3 Major Issues Requiring Expert Intervention</h4>
                  
                  <div className="space-y-6">
                    {/* Issue 1 */}
                    <div className="p-4 bg-red-100 border border-red-300 rounded-lg">
                      <h5 className="font-bold text-red-800 mb-2">🔥 ISSUE #1: Working Capital Crisis</h5>
                      <div className="text-sm text-red-700 space-y-2">
                        <p><strong>Problem:</strong> €380K+ cash unnecessarily locked in inventory/WIP</p>
                        <p><strong>Impact:</strong> Missing growth opportunities, paying unnecessary carrying costs</p>
                        <p><strong>QRM Solution:</strong> Lead time reduction can free 40-60% of working capital</p>
                        <p><strong>Urgency:</strong> Each month delay costs €15K+ in opportunity cost</p>
                      </div>
                    </div>
                    
                    {/* Issue 2 */}
                    <div className="p-4 bg-orange-100 border border-orange-300 rounded-lg">
                      <h5 className="font-bold text-orange-800 mb-2">⚠️ ISSUE #2: Profit Margin Underperformance</h5>
                      <div className="text-sm text-orange-700 space-y-2">
                        <p><strong>Problem:</strong> EBITDA below industry benchmark</p>
                        <p><strong>Gap Analysis:</strong> You're underperforming vs best-in-class manufacturers</p>
                        <p><strong>Revenue Impact:</strong> Significant annual profit left on table</p>
                        <p><strong>QRM Potential:</strong> Process efficiency could significantly improve your margin</p>
                      </div>
                    </div>
                    
                    {/* Issue 3 */}
                    <div className="p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
                      <h5 className="font-bold text-yellow-800 mb-2">⚡ ISSUE #3: Operational Inefficiency Pattern</h5>
                      <div className="text-sm text-yellow-700 space-y-2">
                        <p><strong>Problem:</strong> 45-day cash conversion cycle indicates process bottlenecks</p>
                        <p><strong>Benchmark:</strong> Best-in-class achieve 25-30 days through QRM</p>
                        <p><strong>Hidden Costs:</strong> Long cycles = higher risk, slower response, customer dissatisfaction</p>
                        <p><strong>Competitive Risk:</strong> Faster competitors will steal market share</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Financial Metrics */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">📊 Key Financial Health Indicators</h4>
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Current Ratio:</span>
                        <div className="flex items-center">
                          <span className="font-bold text-yellow-600 mr-2">1.45</span>
                          <Badge className="bg-yellow-100 text-yellow-800">BELOW OPTIMAL</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">Industry benchmark: 1.8-2.2. Your liquidity position is vulnerable.</p>
                    </div>
                    
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">EBITDA Margin:</span>
                        <div className="flex items-center">
                          <span className="font-bold text-red-600 mr-2">12.3%</span>
                          <Badge className="bg-red-100 text-red-800">UNDERPERFORMING</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">Best-in-class manufacturers achieve higher margins through QRM. Significant improvement potential exists.</p>
                    </div>
                    
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Working Capital Efficiency:</span>
                        <div className="flex items-center">
                          <span className="font-bold text-orange-600 mr-2">68%</span>
                          <Badge className="bg-orange-100 text-orange-800">CASH TRAPPED</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">€380K+ cash unnecessarily tied up in inventory and WIP. QRM can free this.</p>
                    </div>
                    
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Cash Conversion Cycle:</span>
                        <div className="flex items-center">
                          <span className="font-bold text-red-600 mr-2">45 days</span>
                          <Badge className="bg-red-100 text-red-800">TOO SLOW</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">Best-in-class: 25-30 days. Your cycle is bleeding €15K+ monthly in opportunity cost.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Required */}
              <div className="mt-8 p-8 bg-gradient-to-r from-purple-100 to-indigo-100 border border-purple-300 rounded-lg">
                <h4 className="text-xl font-bold text-purple-900 mb-4 text-center">
                  🎯 IMMEDIATE ACTION REQUIRED: Expert Discussion
                </h4>
                <p className="text-purple-800 mb-6 leading-relaxed text-center">
                  These findings require immediate attention from QRM expertise. Jan Pauwels (QRM Expert & Consultant) 
                  can provide the strategic guidance needed to transform these 
                  financial weaknesses into competitive advantages.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 mb-1">€1.2M+</div>
                    <div className="text-sm text-gray-600">Annual Improvement Potential</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-green-600 mb-1">18 Months</div>
                    <div className="text-sm text-gray-600">Transformation Timeline</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-1">3-5x</div>
                    <div className="text-sm text-gray-600">Expected ROI</div>
                  </div>
                </div>
                
                <Button 
                  size="lg"
                  className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-semibold text-lg"
                  onClick={sendEmailWithResults}
                >
                  <Award className="mr-3 h-6 w-6" />
                  URGENT: Schedule Strategic Discussion with Jan Pauwels
                </Button>
              </div>
              
              {/* New Analysis Option */}
              <div className="mt-6 text-center">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowResults(false);
                    setAnalysisProgress(0);
                    setUploadedFiles([]);
                    setClientInfo({name: '', function: '', company: '', website: '', email: ''});
                  }}
                >
                  Start New Analysis
                </Button>
              </div>
            </Card>
          )}

          {/* COMPETITIVE BENCHMARKING PREMIUM FEATURE */}
          <Card className="p-8 mt-8 bg-gradient-to-r from-red-50 to-pink-50 border-red-300">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                🔥 Are Your Competitors Outperforming You?
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                <strong>Discover the uncomfortable truth:</strong> How do your margins, efficiency, and cash conversion 
                compare to your 3 biggest competitors? What financial secrets are they using to win your customers?
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-bold text-red-800 mb-4">💀 What You DON'T Know Could Kill Your Business</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1">⚠️</div>
                    <span><strong>Competitor A</strong> might have 40% better cash flow (and you'd never know)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1">⚠️</div>
                    <span><strong>Competitor B</strong> could be operating at 22% EBITDA while you struggle at 12%</span>
                  </li>
                  <li className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1">⚠️</div>
                    <span><strong>Competitor C</strong> may have discovered the working capital secret you're missing</span>
                  </li>
                  <li className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1">🎯</div>
                    <span><strong>The Gap</strong> widens every month while you operate blind</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-green-200">
                <h3 className="text-xl font-bold text-green-800 mb-4">🎯 What Competitive Intelligence Reveals</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <div className="text-green-500 mr-3 mt-1">✨</div>
                    <span><strong>Performance Gaps:</strong> Exactly where you're losing to competitors</span>
                  </li>
                  <li className="flex items-start">
                    <div className="text-green-500 mr-3 mt-1">✨</div>
                    <span><strong>Hidden Opportunities:</strong> What they do better (and how to copy it)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="text-green-500 mr-3 mt-1">✨</div>
                    <span><strong>Strategic Advantages:</strong> Where YOU outperform (leverage this!)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="text-green-500 mr-3 mt-1">🚀</div>
                    <span><strong>Action Plan:</strong> Specific moves to dominate your market</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-100 to-indigo-100 p-8 rounded-lg border border-purple-300 mb-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-purple-900 mb-3">
                  🕵️ COMPETITIVE INTELLIGENCE SUBSCRIPTION
                </h3>
                <p className="text-purple-800 text-lg leading-relaxed">
                  Upload your financials + 3 competitors' balance sheets. Get a confidential report showing exactly where you stand, 
                  what they're doing better, and your precise action plan to overtake them.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-purple-200 mb-6">
                <h4 className="text-lg font-bold text-purple-800 mb-3">💡 Imagine Knowing This About Your Competition:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <p className="text-purple-700 mb-2"><strong>"Competitor A's cash conversion is 18 days faster than yours"</strong></p>
                    <p className="text-purple-700 mb-2"><strong>"Competitor B's EBITDA margin is 8.5 points higher"</strong></p>
                    <p className="text-purple-700"><strong>"Competitor C has €2M less working capital tied up"</strong></p>
                  </div>
                  <div>
                    <p className="text-green-700 mb-2"><strong>"But YOU beat them all on inventory turnover"</strong></p>
                    <p className="text-green-700 mb-2"><strong>"Your ROA is 40% stronger than the market"</strong></p>
                    <p className="text-green-700"><strong>"You have €500K improvement potential they've missed"</strong></p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Button 
                  size="lg"
                  className="px-10 py-4 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-bold text-lg shadow-lg"
                  onClick={() => window.open('mailto:info@smartqrmnavigator.com?subject=COMPETITIVE INTELLIGENCE SUBSCRIPTION - Benchmark Against 3 Competitors&body=Hello Jan,\n\nI want to know exactly how my company compares to my competitors.\n\nPlease send information about:\n• Competitive Intelligence Subscription\n• How to upload competitor financial data securely\n• Monthly pricing and what\'s included\n• Expected timeline for competitive analysis\n\nI need to see where we stand vs the competition.\n\nCompany: [Your Company]\nName: [Your Name]\nEmail: [Your Email]\n\nUrgent request - competitive intelligence needed.', '_blank')}
                >
                  <Target className="mr-3 h-6 w-6" />
                  🔥 GET COMPETITIVE INTELLIGENCE SUBSCRIPTION
                </Button>
                <p className="text-purple-700 text-sm mt-3 font-medium">
                  Limited monthly subscribers • Confidential analysis • Strategic advantage
                </p>
              </div>
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default QRMFinancialLab;
